import { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import { useProgramRoleMenuPermissions } from '../hooks/useProgramRoleMenuPermissions';
import {
  isAuthorizedDashboardRoute,
  resolveDashboardLandingPath,
} from '../dashboard-governance/DashboardLandingResolver';

interface DashboardAccessGuardProps {
  children: ReactNode;
}

export function DashboardAccessGuard({ children }: DashboardAccessGuardProps) {
  const location = useLocation();
  const { programId } = useParams<{ programId: string }>();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin';

  const { dashboardKey, landingRoute, defaultLandingRoute, isLoading } = useProgramRoleMenuPermissions({
    programId,
    enabled: !!programId && !isSuperAdmin,
  });

  if (!programId || isSuperAdmin) {
    return <>{children}</>;
  }

  // Wait for menu permissions to load before making redirect decisions.
  // Without this, default values (workspace) would cause a premature redirect
  // that overrides the user's configured role-based landing route.
  if (isLoading) {
    return <>{children}</>;
  }

  const resolvedLandingRoute = resolveDashboardLandingPath({
    programId,
    dashboardKey: dashboardKey || user?.dashboardKey,
    landingRoute: landingRoute || user?.landingRoute,
    defaultLandingRoute,
  });

  if (isAuthorizedDashboardRoute(resolvedLandingRoute, location.pathname)) {
    return <>{children}</>;
  }

  // Also check the user's auth-context landing route (set at login time
  // from ProgramRoleDashboardMappings).  This covers the case where the
  // hook data hasn't refreshed yet but the login response already resolved
  // the correct landing route.
  if (user?.landingRoute && isAuthorizedDashboardRoute(user.landingRoute, location.pathname)) {
    console.debug('[DashboardAccessGuard] Access granted via auth-context landingRoute', {
      userLandingRoute: user.landingRoute,
      currentPath: location.pathname,
    });
    return <>{children}</>;
  }

  console.debug('[DashboardAccessGuard] Redirecting to resolved landing route', {
    from: location.pathname,
    to: resolvedLandingRoute,
    dashboardKey,
    landingRoute,
    userLandingRoute: user?.landingRoute,
  });

  return <Navigate to={resolvedLandingRoute} replace />;
}

export default DashboardAccessGuard;